import { unwrap, type Schemas } from '@mrigl/api-client';
import type { Locale } from '@mrigl/shared';
import { Alert, Button, SelectField, TextAreaField, TextField } from '@mrigl/ui';
import { useEffect, useMemo, useState, type SubmitEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n } from '../../i18n.js';
import {
  catalogError,
  cleanText,
  display,
  moveItem,
  newKey,
  storeLocales,
  toMajor,
  toMinor,
  useStore,
  type Localized,
  type StoreDetails,
} from './shared.js';

type Product = Schemas['Product'];
type ProductInput = Schemas['ProductInput'];
type MediaAsset = Schemas['MediaAsset'];
type Status = Product['status'];
type TaxClass = Schemas['CheckoutSettings']['taxes']['classes'][number];

const MAX_VARIANTS = 100;
const STATUSES: readonly Status[] = ['draft', 'active', 'archived'];

interface OptionDraft {
  readonly name: Localized;
  readonly values: readonly { readonly key: string; readonly label: Localized }[];
}

interface VariantDraft {
  readonly variantId?: string;
  readonly price: string;
  readonly compareAt: string;
  readonly sku: string;
  readonly barcode: string;
  readonly weight: string;
}

interface ImageDraft {
  readonly mediaId: string;
  readonly url: string;
  readonly name: string;
}

interface Draft {
  readonly title: Localized;
  readonly description: Localized;
  readonly handle: string;
  readonly status: Status;
  readonly tags: string;
  /** Tax class key from the checkout settings; '' = the store default. */
  readonly taxClass: string;
  readonly options: readonly OptionDraft[];
  /** Keyed by JSON of the option value keys (the combination). */
  readonly variants: Readonly<Record<string, VariantDraft>>;
  readonly images: readonly ImageDraft[];
}

const EMPTY_VARIANT: VariantDraft = { price: '', compareAt: '', sku: '', barcode: '', weight: '' };

function emptyDraft(): Draft {
  return {
    title: {},
    description: {},
    handle: '',
    status: 'draft',
    tags: '',
    taxClass: '',
    options: [],
    variants: { '[]': EMPTY_VARIANT },
    images: [],
  };
}

function fromProduct(product: Product): Draft {
  return {
    title: product.title,
    description: product.description,
    handle: product.handle,
    status: product.status,
    tags: product.tags.join(', '),
    taxClass: product.taxClass ?? '',
    options: product.options.map((option) => ({ name: option.name, values: option.values })),
    variants: Object.fromEntries(
      product.variants.map((variant) => [
        JSON.stringify(variant.optionValues),
        {
          variantId: variant.variantId,
          price: toMajor(variant.price.minor),
          compareAt: variant.compareAtPrice === null ? '' : toMajor(variant.compareAtPrice.minor),
          sku: variant.sku ?? '',
          barcode: variant.barcode ?? '',
          weight: variant.weightGrams === null ? '' : String(variant.weightGrams),
        },
      ]),
    ),
    images: product.media.map((image) => ({
      mediaId: image.mediaId,
      url: image.thumbnailUrl,
      name: image.mediaId,
    })),
  };
}

/** Every combination of option value keys (one value per option). */
export function combinations(options: readonly OptionDraft[]): string[][] {
  return options.reduce<string[][]>(
    (acc, option) => acc.flatMap((prefix) => option.values.map((value) => [...prefix, value.key])),
    [[]],
  );
}

/** Product create/edit. `productId` absent = new product. */
export function ProductEditorPage() {
  const { storeId = '', productId } = useParams();
  const { t } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [product, setProduct] = useState<Product | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (productId === undefined) return;
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/products/{productId}', {
        params: { path: { storeId, productId } },
      }),
    )
      .then(setProduct)
      .catch((failure: unknown) => {
        setLoadError(errorMessage(t, failure));
      });
  }, [storeId, productId, t]);

  const error = storeError ?? loadError;
  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (store === null || (productId !== undefined && product === null)) return <Loading />;
  return (
    <Editor
      key={product?.productId ?? 'new'}
      store={store}
      product={product}
      onSaved={setProduct}
    />
  );
}

function Editor({
  store,
  product,
  onSaved,
}: {
  readonly store: StoreDetails;
  readonly product: Product | null;
  readonly onSaved: (product: Product) => void;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const locales = storeLocales(store);
  const canManage = store.permissions.includes('catalog.manage');
  const [draft, setDraft] = useState<Draft>(() =>
    product === null ? emptyDraft() : fromProduct(product),
  );
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [taxClasses, setTaxClasses] = useState<TaxClass[]>([]);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/checkout-settings', {
        params: { path: { storeId: store.storeId } },
      }),
    )
      .then((state) => {
        setTaxClasses(state.settings.taxes.enabled ? state.settings.taxes.classes : []);
      })
      .catch(() => {
        // Without the settings the product keeps its class; the select lists only it.
      });
  }, [store.storeId]);

  const combos = useMemo(() => combinations(draft.options), [draft.options]);
  const tooMany = combos.length > MAX_VARIANTS;
  const update = (change: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };
  const setOptions = (options: OptionDraft[]) => {
    update({ options });
  };
  const variantLabel = (combo: readonly string[]) =>
    combo
      .map((key, position) => {
        const value = draft.options[position]?.values.find((candidate) => candidate.key === key);
        return value === undefined ? key : display(value.label, locale, store, key);
      })
      .join(' / ') || t('admin.product.variant');

  function buildInput(): ProductInput | null {
    const errors: Record<string, string> = {};
    const variants = combos.map((combo) => {
      const key = JSON.stringify(combo);
      const variant = draft.variants[key] ?? EMPTY_VARIANT;
      const price = toMinor(variant.price);
      const compareAt = variant.compareAt.trim() === '' ? null : toMinor(variant.compareAt);
      if (price === null) errors[`${key}:price`] = t('admin.product.invalidPrice');
      if (variant.compareAt.trim() !== '' && compareAt === null) {
        errors[`${key}:compareAt`] = t('admin.product.invalidPrice');
      }
      return {
        ...(variant.variantId === undefined ? {} : { variantId: variant.variantId }),
        optionValues: combo,
        sku: variant.sku.trim() === '' ? null : variant.sku.trim(),
        barcode: variant.barcode.trim() === '' ? null : variant.barcode.trim(),
        price: { minor: price ?? '0', currency: 'TND' as const },
        compareAtPrice: compareAt === null ? null : { minor: compareAt, currency: 'TND' as const },
        weightGrams: /^[0-9]+$/.test(variant.weight.trim()) ? Number(variant.weight.trim()) : null,
      };
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return null;
    return {
      ...(draft.handle.trim() === '' ? {} : { handle: draft.handle.trim().toLowerCase() }),
      status: draft.status,
      title: cleanText(draft.title),
      description: cleanText(draft.description),
      tags: draft.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag !== ''),
      taxClass: draft.taxClass === '' ? null : draft.taxClass,
      options: draft.options.map((option) => ({
        name: cleanText(option.name),
        values: option.values.map((value) => ({ key: value.key, label: cleanText(value.label) })),
      })),
      variants,
      mediaIds: draft.images.map((image) => image.mediaId),
    };
  }

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    setError(null);
    const input = buildInput();
    if (input === null) {
      setError(t('admin.product.error.validation'));
      return;
    }
    setBusy(true);
    try {
      if (product === null) {
        const created = await unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/products', {
            params: { path: { storeId: store.storeId } },
            body: input,
          }),
        );
        void navigate(`/stores/${store.storeId}/products/${created.productId}`, {
          replace: true,
          state: { notice: t('admin.product.saved') },
        });
      } else {
        const saved = await unwrap(
          api.PUT('/admin-api/v1/stores/{storeId}/products/{productId}', {
            params: { path: { storeId: store.storeId, productId: product.productId } },
            body: { ...input, version: product.version },
          }),
        );
        onSaved(saved);
        setDraft(fromProduct(saved));
        setNotice(t('admin.product.saved'));
      }
    } catch (failure) {
      setError(catalogError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (product === null) return;
    setBusy(true);
    try {
      await unwrap(
        api.DELETE('/admin-api/v1/stores/{storeId}/products/{productId}', {
          params: { path: { storeId: store.storeId, productId: product.productId } },
        }),
      );
      void navigate(`/stores/${store.storeId}/products`, { replace: true });
    } catch (failure) {
      setError(catalogError(t, failure));
      setBusy(false);
    }
  }

  const languageName = (code: Locale) => t(`admin.language.${code}`);
  const heading =
    product === null
      ? t('admin.product.new')
      : display(product.title, locale, store, product.handle);

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${store.storeId}/products`}>{t('admin.catalog.products')}</Link>
      </p>
      <h1 className="mrigl-heading">{heading}</h1>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form
        className="mrigl-stack"
        aria-label={product === null ? t('admin.product.new') : t('admin.product.edit')}
        onSubmit={(event) => void save(event)}
      >
        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.product.details')}</legend>
          {locales.map((code) => (
            <TextField
              key={`title-${code}`}
              lang={code}
              dir="auto"
              label={t('admin.product.titleIn', { language: languageName(code) })}
              maxLength={255}
              required={code === store.defaultLocale}
              value={draft.title[code] ?? ''}
              onChange={(event) => {
                update({ title: { ...draft.title, [code]: event.target.value } });
              }}
            />
          ))}
          {locales.map((code) => (
            <TextAreaField
              key={`description-${code}`}
              lang={code}
              dir="auto"
              label={t('admin.product.descriptionIn', { language: languageName(code) })}
              maxLength={10_000}
              value={draft.description[code] ?? ''}
              onChange={(event) => {
                update({ description: { ...draft.description, [code]: event.target.value } });
              }}
            />
          ))}
          <SelectField
            label={t('admin.product.column.status')}
            value={draft.status}
            onChange={(event) => {
              update({ status: event.target.value as Status });
            }}
            options={STATUSES.map((value) => ({
              value,
              label: t(`admin.product.status.${value}`),
            }))}
          />
          <TextField
            ltr
            label={t('admin.product.handle')}
            hint={t('admin.product.handleHint')}
            maxLength={100}
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            value={draft.handle}
            onChange={(event) => {
              update({ handle: event.target.value });
            }}
          />
          <TextField
            label={t('admin.product.tags')}
            hint={t('admin.product.tagsHint')}
            value={draft.tags}
            onChange={(event) => {
              update({ tags: event.target.value });
            }}
          />
          {taxClasses.length === 0 && draft.taxClass === '' ? null : (
            <SelectField
              label={t('admin.product.taxClass')}
              value={draft.taxClass}
              onChange={(event) => {
                update({ taxClass: event.target.value });
              }}
              options={[
                { value: '', label: t('admin.product.taxClass.default') },
                ...taxClasses.map((item) => ({
                  value: item.key,
                  label: `${display(item.name, locale, store, item.key)} (${String(item.rateBp / 100)} %)`,
                })),
                ...(draft.taxClass === '' || taxClasses.some((item) => item.key === draft.taxClass)
                  ? []
                  : [
                      {
                        value: draft.taxClass,
                        label: t('admin.product.taxClass.unknown', { key: draft.taxClass }),
                      },
                    ]),
              ]}
            />
          )}
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.product.images')}</legend>
          {draft.images.length === 0 ? (
            <p className="mrigl-muted">{t('admin.product.noImages')}</p>
          ) : null}
          <ul className="admin-image-list">
            {draft.images.map((image, index) => (
              <li key={image.mediaId} className="admin-image-list__item">
                <img src={image.url} alt="" width={64} height={64} />
                <div className="admin-actions">
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label={t('admin.product.moveUp', { name: String(index + 1) })}
                    disabled={index === 0}
                    onClick={() => {
                      update({ images: moveItem(draft.images, index, -1) });
                    }}
                  >
                    <span aria-hidden="true">↑</span>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label={t('admin.product.moveDown', { name: String(index + 1) })}
                    disabled={index === draft.images.length - 1}
                    onClick={() => {
                      update({ images: moveItem(draft.images, index, 1) });
                    }}
                  >
                    <span aria-hidden="true">↓</span>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label={t('admin.product.removeImage', { name: String(index + 1) })}
                    onClick={() => {
                      update({ images: draft.images.filter((_, other) => other !== index) });
                    }}
                  >
                    <span aria-hidden="true">✕</span>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          {pickerOpen ? (
            <MediaPicker
              storeId={store.storeId}
              selected={draft.images.map((image) => image.mediaId)}
              onPick={(asset) => {
                update({
                  images: [
                    ...draft.images,
                    {
                      mediaId: asset.mediaId,
                      url: asset.variants[0]?.url ?? asset.url,
                      name: asset.filename,
                    },
                  ],
                });
              }}
              onClose={() => {
                setPickerOpen(false);
              }}
            />
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPickerOpen(true);
              }}
            >
              {t('admin.product.addImages')}
            </Button>
          )}
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.product.options')}</legend>
          <p className="mrigl-muted">{t('admin.product.optionsHint')}</p>
          {draft.options.map((option, position) => {
            const number = position + 1;
            const setOption = (next: OptionDraft) => {
              setOptions(draft.options.map((other, index) => (index === position ? next : other)));
            };
            return (
              <div key={`option-${String(position)}`} className="admin-option mrigl-stack">
                {locales.map((code) => (
                  <TextField
                    key={code}
                    lang={code}
                    dir="auto"
                    maxLength={60}
                    label={t('admin.product.optionNameIn', {
                      number,
                      language: languageName(code),
                    })}
                    value={option.name[code] ?? ''}
                    onChange={(event) => {
                      setOption({
                        ...option,
                        name: { ...option.name, [code]: event.target.value },
                      });
                    }}
                  />
                ))}
                <fieldset className="mrigl-stack">
                  <legend>{t('admin.product.optionValues', { number })}</legend>
                  {option.values.map((value, index) => (
                    <div key={value.key} className="admin-option__value">
                      {locales.map((code) => (
                        <TextField
                          key={code}
                          lang={code}
                          dir="auto"
                          maxLength={60}
                          label={t('admin.product.valueIn', {
                            index: index + 1,
                            language: languageName(code),
                          })}
                          value={value.label[code] ?? ''}
                          onChange={(event) => {
                            setOption({
                              ...option,
                              values: option.values.map((other) =>
                                other.key === value.key
                                  ? {
                                      ...other,
                                      label: { ...other.label, [code]: event.target.value },
                                    }
                                  : other,
                              ),
                            });
                          }}
                        />
                      ))}
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={option.values.length === 1}
                        onClick={() => {
                          setOption({
                            ...option,
                            values: option.values.filter((other) => other.key !== value.key),
                          });
                        }}
                      >
                        {t('admin.product.removeValue', { index: index + 1 })}
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setOption({
                        ...option,
                        values: [...option.values, { key: newKey(), label: {} }],
                      });
                    }}
                  >
                    {t('admin.product.addValue')}
                  </Button>
                </fieldset>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setOptions(draft.options.filter((_, index) => index !== position));
                  }}
                >
                  {t('admin.product.removeOption', { number })}
                </Button>
              </div>
            );
          })}
          {draft.options.length < 3 ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setOptions([
                  ...draft.options,
                  { name: {}, values: [{ key: newKey(), label: {} }] },
                ]);
              }}
            >
              {t('admin.product.addOption')}
            </Button>
          ) : null}
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.product.variants')}</legend>
          {tooMany ? (
            <Alert tone="error">{t('admin.product.tooManyVariants', { max: MAX_VARIANTS })}</Alert>
          ) : (
            combos.map((combo) => {
              const key = JSON.stringify(combo);
              const variant = draft.variants[key] ?? EMPTY_VARIANT;
              const label = variantLabel(combo);
              const setVariant = (change: Partial<VariantDraft>) => {
                update({ variants: { ...draft.variants, [key]: { ...variant, ...change } } });
              };
              return (
                <div key={key} className="admin-variant">
                  {combo.length === 0 ? null : <p className="admin-variant__label">{label}</p>}
                  <TextField
                    ltr
                    inputMode="decimal"
                    required
                    label={
                      combo.length === 0
                        ? t('admin.product.price')
                        : t('admin.product.priceFor', { variant: label })
                    }
                    error={fieldErrors[`${key}:price`]}
                    value={variant.price}
                    onChange={(event) => {
                      setVariant({ price: event.target.value });
                    }}
                  />
                  <TextField
                    ltr
                    inputMode="decimal"
                    label={
                      combo.length === 0
                        ? t('admin.product.compareAt')
                        : t('admin.product.compareAtFor', { variant: label })
                    }
                    error={fieldErrors[`${key}:compareAt`]}
                    value={variant.compareAt}
                    onChange={(event) => {
                      setVariant({ compareAt: event.target.value });
                    }}
                  />
                  <TextField
                    ltr
                    maxLength={64}
                    label={
                      combo.length === 0
                        ? t('admin.product.sku')
                        : t('admin.product.skuFor', { variant: label })
                    }
                    value={variant.sku}
                    onChange={(event) => {
                      setVariant({ sku: event.target.value });
                    }}
                  />
                  {combo.length === 0 ? (
                    <>
                      <TextField
                        ltr
                        maxLength={64}
                        label={t('admin.product.barcode')}
                        value={variant.barcode}
                        onChange={(event) => {
                          setVariant({ barcode: event.target.value });
                        }}
                      />
                      <TextField
                        ltr
                        inputMode="numeric"
                        label={t('admin.product.weight')}
                        value={variant.weight}
                        onChange={(event) => {
                          setVariant({ weight: event.target.value });
                        }}
                      />
                    </>
                  ) : null}
                </div>
              );
            })
          )}
        </fieldset>

        {canManage ? (
          <Button type="submit" busy={busy} disabled={tooMany}>
            {t('admin.product.save')}
          </Button>
        ) : null}
      </form>
      {canManage && product !== null ? (
        <div className="admin-actions">
          {confirmDelete ? (
            <>
              <Button variant="danger" busy={busy} onClick={() => void remove()}>
                {t('admin.product.confirmDelete')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirmDelete(false);
                }}
              >
                {t('admin.media.cancel')}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              {t('admin.product.delete')}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Media library browser for adding images to a product. */
function MediaPicker({
  storeId,
  selected,
  onPick,
  onClose,
}: {
  readonly storeId: string;
  readonly selected: readonly string[];
  readonly onPick: (asset: MediaAsset) => void;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const [items, setItems] = useState<MediaAsset[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/media', {
        params: { path: { storeId }, query: { limit: 100 } },
      }),
    )
      .then((page) => {
        setItems(page.items);
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
        setItems([]);
      });
  }, [storeId, t]);

  return (
    <div className="mrigl-stack admin-picker">
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {items === null ? (
        <Loading />
      ) : items.length === 0 ? (
        <p className="mrigl-muted">
          {t('admin.media.empty')}{' '}
          <Link to={`/stores/${storeId}/media`}>{t('admin.media.title')}</Link>
        </p>
      ) : (
        <ul className="admin-media-grid">
          {items.map((item) => (
            <li key={item.mediaId} className="mrigl-card admin-media-card">
              <img src={item.variants[0]?.url ?? item.url} alt="" width={160} height={160} />
              <Button
                type="button"
                variant="secondary"
                disabled={selected.includes(item.mediaId)}
                onClick={() => {
                  onPick(item);
                }}
              >
                {t('admin.product.selectImage', { name: item.filename })}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button type="button" variant="secondary" onClick={onClose}>
        {t('admin.product.closePicker')}
      </Button>
    </div>
  );
}
